import { FC } from 'react'
import { Box } from '@mui/material'
import { GuideSection } from '../core'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'
import { typeScale } from '@/shared/styles/typography'

type SectionStepsProps = {
  steps: GuideSection['steps']
}

/**
 * 区分の中の手順の索引。**スマホだけ**に出す (PC は左の固定目次が同じ仕事をする)。
 *
 * 区分の骨格は「上から順に試して、直ったところで止める」なので、手順が 3〜4 本ある
 * 区分では「あと何本あるか・どこまで来たか」が見えないと骨格が働かない。一方で
 * 全体の目次をそのままスマホに出すと、区分 6 + 手順 14 = 20 項目で見出しが折り返し、
 * 最初の 1 画面がまるごと索引になる。冒頭の区分ルーターが「どこを見るか」を決める
 * 仕事をしているので、索引は**自分が入った区分の中にだけ**置く。
 *
 * 常駐しないので現在地の追従 (scroll-spy) は持たせない。出どころは PC の目次と同じ
 * `sections[].steps` で、md の見出しを直せば両方が追従する。design-book ADR-0045。
 */
export const SectionSteps: FC<SectionStepsProps> = ({ steps }) => {
  if (steps.length === 0) return null

  return (
    <Box
      component="nav"
      aria-label="この区分の手順"
      sx={{ display: { xs: 'block', md: 'none' }, mb: 3 }}
    >
      {steps.map((step) => (
        <Box
          key={step.id}
          component="a"
          href={`#${step.id}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            // 指で押す行なので、字の大きさでなく行の高さで当たり判定を確保する
            minHeight: 44,
            px: 1.5,
            py: 0.5,
            borderRadius: `${RADIUS.lg}px`,
            textDecoration: 'none',
            ...typeScale.bodySm,
            color: colorTokens.fg.muted,
            '&:hover': { backgroundColor: colorTokens.overlay.hover },
          }}
        >
          {step.title}
        </Box>
      ))}
    </Box>
  )
}
